import { useEffect, useState } from 'react'
import { supabase } from '../supabase'
import { useToast } from '../Toast'
import { useUser } from '../UserContext'

export default function Profile() {
  const toast = useToast()
  const { user, profile, org, isAdmin, refreshProfile } = useUser()
  const [fullName, setFullName] = useState('')
  const [savingProfile, setSavingProfile] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [savingPassword, setSavingPassword] = useState(false)

  useEffect(() => {
    setFullName(profile?.full_name || '')
  }, [profile?.full_name])

  async function saveProfile(event) {
    event.preventDefault()

    if (!profile?.id) {
      toast.error('Profile is still loading')
      return
    }

    const trimmedName = fullName.trim()

    if (!trimmedName) {
      toast.error('Full name is required')
      return
    }

    setSavingProfile(true)

    const { error } = await supabase
      .from('profiles')
      .update({ full_name: trimmedName })
      .eq('id', profile.id)

    if (error) {
      toast.error(`Failed to update profile: ${error.message}`)
      setSavingProfile(false)
      return
    }

    await refreshProfile?.()
    toast.success('Profile updated')
    setSavingProfile(false)
  }

  async function changePassword(event) {
    event.preventDefault()

    if (newPassword.length < 12) {
      toast.error('Password must be at least 12 characters')
      return
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    setSavingPassword(true)

    const { error } = await supabase.auth.updateUser({
      password: newPassword
    })

    if (error) {
      toast.error(`Failed to update password: ${error.message}`)
      setSavingPassword(false)
      return
    }

    setNewPassword('')
    setConfirmPassword('')
    toast.success('Password updated')
    setSavingPassword(false)
  }

  return (
    <div className="hv-page" style={{ maxWidth: '1040px' }}>
      <div className="hv-page-header">
        <div>
          <h1 className="hv-page-title">Account</h1>
          <p className="hv-page-subtitle">Manage your personal profile and sign-in details</p>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        gap: '24px'
      }}>
        <form className="hv-card" style={{ padding: '24px' }} onSubmit={saveProfile}>
          <div style={{ marginBottom: '22px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: 800, color: 'var(--hv-text-primary)', margin: 0 }}>
              Profile
            </h2>
            <p style={{ fontSize: '13px', color: 'var(--hv-text-muted)', margin: '6px 0 0' }}>
              Your organisation is controlled by workspace administrators.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '16px',
            marginBottom: '18px'
          }}>
            <div>
              <label className="hv-form-label">Full Name</label>
              <input
                className="hv-input"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                placeholder="e.g. Alex Morgan"
              />
            </div>

            <div>
              <label className="hv-form-label">Email</label>
              <input
                className="hv-input"
                value={profile?.email || user?.email || ''}
                disabled
                readOnly
              />
            </div>

            <div>
              <label className="hv-form-label">Organisation</label>
              <input
                className="hv-input"
                value={org?.name || 'Current organisation'}
                disabled
                readOnly
              />
            </div>

            <div>
              <label className="hv-form-label">Role</label>
              <input
                className="hv-input"
                value={isAdmin ? 'Admin' : 'Member'}
                disabled
                readOnly
              />
            </div>
          </div>

          <button className="hv-btn hv-btn-primary" type="submit" disabled={savingProfile}>
            {savingProfile ? 'Saving...' : 'Save Profile'}
          </button>
        </form>

        <form className="hv-card" style={{ padding: '24px' }} onSubmit={changePassword}>
          <div style={{ marginBottom: '22px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: 800, color: 'var(--hv-text-primary)', margin: 0 }}>
              Password
            </h2>
            <p style={{ fontSize: '13px', color: 'var(--hv-text-muted)', margin: '6px 0 0' }}>
              Update your password while you are signed in.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '16px',
            marginBottom: '18px'
          }}>
            <div>
              <label className="hv-form-label">New Password</label>
              <input
                className="hv-input"
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>

            <div>
              <label className="hv-form-label">Confirm Password</label>
              <input
                className="hv-input"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>
          </div>

          <button className="hv-btn hv-btn-primary" type="submit" disabled={savingPassword}>
            {savingPassword ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  )
}
